import { Box, CircularProgress, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux-latest';
import { useNavigate } from 'react-router-dom';
import { configureProfile } from '../../../redux/actions';
import { AppDispatch } from '../../../redux/store/configureStore';
import useDefineOrganizationForConfig from '../hooks/use-define-organization-for-config';
import FeedbackMessage from '../../../components/form/feedback-message';
import FormInputField from '../../../components/form/form-input-field';
import LoadingButton from '../../../components/form/loading-button';
import { useMsal } from '@azure/msal-react';

type ConfigureProfileFormValues = {
    firstName: string;
    lastName: string;
};

const ConfigureProfileForm = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const msal = useMsal();

    const [authErrorMessage, setAuthErrorMessage] = useState<string | null>(null);
    const { register, handleSubmit, formState, setValue, trigger } = useForm<ConfigureProfileFormValues>({
        defaultValues: {
            firstName: '',
            lastName: '',
        },
        mode: 'onTouched',
    });
    const { errors } = formState;
    const { organization } = useDefineOrganizationForConfig();
    const { mutate, status: formStatus } = useMutation({
        mutationFn: async (data: ConfigureProfileFormValues) => {
            return await dispatch(configureProfile(msal.instance, organization, { ...data }, navigate));
        },
        onError: (error: unknown) => {
            if (error instanceof Error) {
                setAuthErrorMessage(error.message);
            } else {
                setAuthErrorMessage('An error occurred');
            }
        },
    });

    const onSubmit = (data: ConfigureProfileFormValues) => {
        mutate(data);
    };
    const handleKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
        }
    };

    const isDisabled = !formState.isValid || status === 'pending';
    const isLoading = formStatus === 'pending' || status === 'pending';

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)} onKeyDown={handleKeyDown} noValidate>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <FormInputField<'firstName'>
                        id="firstName"
                        label="First name"
                        register={register('firstName', {
                            required: 'First name is required',
                        })}
                        error={errors.firstName?.message}
                        placeholder="John"
                        type="text"
                        isLoading={isLoading}
                    />
                    <FormInputField<'lastName'>
                        id="lastName"
                        label="Last name"
                        register={register('lastName', {
                            required: 'Last name is required',
                        })}
                        error={errors.lastName?.message}
                        placeholder="Doe"
                        type="text"
                        isLoading={isLoading}
                    />
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        marginTop: '24px',
                        gap: '16px',
                    }}
                >
                    <LoadingButton
                        isLoading={formStatus === 'pending' || status === 'pending'}
                        disabled={isDisabled || isLoading}
                        type="submit"
                    >
                        {isLoading ? (
                            <CircularProgress size={24} sx={{ color: '#969FAC' }} />
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: '8px',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography
                                    component={'span'}
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        lineHeight: '21px',
                                        color: 'white',
                                    }}
                                >
                                    Next
                                </Typography>
                                <ArrowRight size={20} stroke={'#fff'} />
                            </Box>
                        )}
                    </LoadingButton>
                    <FeedbackMessage id="configure-profile-form-feedback-message" error={authErrorMessage} info={''} />
                </Box>
            </form>
            {/* <DevTool control={control} /> */}
        </>
    );
};

export default ConfigureProfileForm;
